import React, { useEffect, useState } from 'react'

import { useNavigate, useParams } from 'react-router-dom'

// Grommet
import { ResponsiveContext } from 'grommet'

import { capitalize } from '../../utils/text'
import { getObjectByValue } from '../../utils/objects'

// Spinner
import PulseLoader from 'react-spinners/PulseLoader'

// Services
import LocalStorageService from '../../services/LocalStorageService'
import NotificationService from '../../services/NotificationService'
import UserService from '../../services/UserService'
import UserTagService from '../../services/UserTagService'
import OrganisationTagService from '../../services/OrganisationTagService'

// Font Awesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// Appt Components
import Anchor from '../../components/simple/anchor/Anchor'
import Box from '../../components/simple/box/Box'
import DataTable from '../../components/simple/dataTable/DataTable.js'
import Text from '../../components/simple/text/Text'

import NotificationLayer from '../../components/compound/notification/NotificationLayer'

// Shared Components
import BreadcrumbBar from '../shared/BreadcrumbBar'
import ActionButton from '../../components/compound/actionButton/ActionButton.js'
import Divider from '../../components/simple/divider/Divider.js'

import ActionMenu from '../../components/compound/actionMenu/ActionMenu.js'


function ServiceUserList (props) {
  const [userValues, setUserValues] = useState([])
  const [filteredUsers, setFilteredUsers] = useState([])

  const navigate = useNavigate()
  const [userToDelete, setUserToDelete] = useState(null)
  const [showDeleteOptions, showDelete] = useState(false)
  // const { error, showError } = useErrorHandler(null)
  const [loading, setLoading] = useState(false)
  const [hasTerms, setHasTerms] = useState(false)

  const [showFormerStudents, setShowFormerStudents] = useState(false)

  const [yearGroups, setYearGroups] = useState([])
  const [selectedYearGroup, setSelectedYearGroup] = useState()

  const params = useParams()

  let filterType = params.filter
  let filterValue = params.by

  const apiToken = LocalStorageService.get('apiToken')


  // DataTable sort state
  const [sort, setSort] = useState({
    property: 'learnerNumber',
    direction: 'asc'
  })

  const terms = LocalStorageService.get('terms')

  const activeOrg = LocalStorageService.get('activeOrg')

  // Prompt user to delete
  const showDeleteUserOptions = (user) => {
    setUserToDelete(user)
    showDelete(true)
  }

  // Delete a User
  const deleteUser = async () => {
    const apiToken = LocalStorageService.get('apiToken')
    // Delete from server
    const response = await UserService.deleteUser(apiToken, userToDelete)
    if (response.data.success === 'true') {
      // And from state to force a refresh
      const newUserValues = userValues.filter((item) => item.id !== userToDelete.id)
      setUserValues(newUserValues)
      NotificationService.success(`${terms.serviceUser} deleted`)
    } else {
      NotificationService.error(response.error)
    }
    showDelete(false)
  }

  useEffect(() => {

    const checkForTerms = async () => {

      const params = { fields: 'type,key,value' }
      const where = { type: 'besmart' }
      const organisationTags = await OrganisationTagService.get(apiToken, params, where)

      // Override terms if set
      if (organisationTags) {
        const academicYearStartDay = organisationTags.find(tag => (tag.type === 'besmart' && tag.key === 'academicYearStartDay'))
        setHasTerms(academicYearStartDay?.value?.length ? true : false)
      }
    }
    checkForTerms()


  }, [])

  useEffect(() => {

    if (selectedYearGroup) {
      const filtered = userValues.filter(user => user.yearGroup === selectedYearGroup)
      setFilteredUsers(filtered)
    } else {
      setFilteredUsers(userValues)
    }
  }, [selectedYearGroup, userValues])


  useEffect(() => {
    if (filterType && filterValue) {

      if (filterType === 'year') {
        setSelectedYearGroup(filterValue)
      }
    }

  }, [filterType, filterValue, userValues])


  useEffect(() => {
    getGroups()
  }, [])

   const getGroups = async () => {
      // Now find all groups
      const parsedAcademicYears = await OrganisationTagService.getAcademicYears(apiToken, props.activeOrg)
      setYearGroups(parsedAcademicYears)
    }

  // Basically componentDidMount
  useEffect(() => {
    let unmounted = false

    // Get Users for this organisation
    const getUsers = async () => {
      const apiToken = LocalStorageService.get('apiToken')

      let params = {
        fields: 'id,firstName,lastName,reference',
        limit: 2000,
        orgId: activeOrg.id,
        type: 'student',
        showFormerStudents: showFormerStudents
        // where: { type: 'student' }
      }

      setLoading(true)
      setUserValues([])
      const users = await UserService.getUsers(apiToken, params)
      if (users?.error) {
        // NotificationService.error(users.error)
      } else {
        if (!unmounted) {
          if (users?.error) {
            setUserValues(null)
          } else if (users?.data) {
            // Now get the academicYear tags and match them to the User
            params = {
              fields: 'id,user,value,key,organisation',
              limit: 2000,
              orgId: activeOrg.id,
              type: 'student'
            }

            const where = {
              key: 'academicYear'
            }
            const yearGroups = await UserTagService.get(apiToken, params, where)

            users.data.forEach(user => {
              const userInGroup = getObjectByValue(yearGroups, 'user', user.id)
              if (userInGroup) {
                user.yearGroup = userInGroup.value
              }
            })

            const mappedUsers = users.data.map((data, index) => ({
              id: data.id,
              learnerNumber: data.reference,
              name: data.firstName + ' ' + data.lastName,
              yearGroup: data.yearGroup
            }))

            setUserValues(mappedUsers)
          }
        }
      }
    }

    getUsers()
    setLoading(false)

    return () => { unmounted = true }
  }, [showFormerStudents])

  const dataColumns = [
    {
      property: 'name',
      header: <Text weight={600}>Name</Text>,
      render: datum => (
        <Anchor href={`/serviceuser/${datum.id}/profile`} color='primary'>
          <Text>{datum.name}</Text>
        </Anchor>
      ),
      primary: true,
      search: true
    },
    {
      property: 'learnerNumber',
      header: <Text weight={600}>Learner Number</Text>,
      primary: true,
      search: true
    },
    {
      property: 'yearGroup',
      header: <Text weight={600}>Year Group</Text>,
      render: datum => (
        <Anchor onClick={() => { setSelectedYearGroup(datum.yearGroup) }} color='primary'>
          <Text>{`${datum.yearGroup || ''}`}</Text>
        </Anchor>
      ),
      search: true
    },
    {
      property: 'actions',
      header: <Text weight={600}>Actions</Text>,
      render: datum => (
        <Box direction='row'>
          <ActionButton
            href={`/serviceuser/${datum.id}/profile`}
            target='_self'
            plain
            children={
              <Box pad='5px'>
                <FontAwesomeIcon icon={['fal', 'eye']} color='#ff8300' />
              </Box>
            }
          />
          <ActionButton
            target='_self'
            plain
            onClick={() => showDeleteUserOptions(datum)}
            children={
              <Box pad='5px'>
                <FontAwesomeIcon icon={['fal', 'trash']} color='#ff8300' />
              </Box>
            }
          />
        </Box>
      ),
      sortable: false
    }
  ]

  return (
    <>
      {showDeleteOptions &&
        <NotificationLayer button1Text='Yes' button1Click={deleteUser} button2Text='No' button2Click={() => { showDelete(false) }}>Are you sure you want to delete user {userToDelete.name}?</NotificationLayer>}
      <BreadcrumbBar
        title={`${capitalize(terms.serviceUser || 'Service User')} Directory`}
          path={<><Anchor href='/'>BehaviourSmart</Anchor><Text color='besmartBlue' size='xsmall'>{window.location.pathname}</Text></>}
        />

       <Box direction='row' justify='end' gap='none' pad={{ vertical: 'none', bottom: 'none' }} margin={{ bottom: 'medium' }}>
          <Box direction='row' align='center' gap='small' margin={{ right: 'small' }}>
            <Text color='white' size="large" weight='bold'>Actions:</Text>
          </Box>
          <ActionButton href='/serviceuser/new/edit' label={<Text><FontAwesomeIcon icon={['fal', 'plus-circle']} /> Add {terms.serviceUser || 'Service User'}</Text>} target='_self' />
          <ActionButton href='/serviceuser/new/upload' label={<Text><FontAwesomeIcon icon={['fal', 'cloud-upload']} /> Bulk Upload</Text>}  />
          {/* <ActionButton label='Record & Plan' labelIcon={['fal', 'plus-circle']} onClick={() => console.log('sdf')} target='_self' /> */}
      </Box>

      <Box background='white' direction='column' flex='grow' gap='small' pad='medium' round='small'>
        <Box direction='column'>

           <Box direction='row' justify='end' gap='none' pad={{ vertical: 'none', bottom: 'none' }} margin={{ bottom: 'medium' }}>
          <Box direction='row' align='center' gap='small' margin={{ right: 'small' }}>
            <Text color='besmartBlue' size="large" weight='bold'>Filters:</Text>
          </Box>


            <ActionMenu
              label={
                `${showFormerStudents ? 'Former' : 'Active'} ${terms.serviceUserPlural || 'Service Users'}`
              }
              items={[
                { label: `Active ${terms.serviceUserPlural || 'Service Users'}`, onClick: () => { setShowFormerStudents(false) } },
                { label: `Former ${terms.serviceUserPlural || 'Service Users'}`, onClick: () => { setShowFormerStudents(true) } },
              ]}
            />

           <ActionMenu
              label={
                `${selectedYearGroup || 'All Year Groups'}`
              }
              items={[
                { label: `All Year Groups`, onClick: () => { setSelectedYearGroup(null) } },
                ...yearGroups.map(group => ({
                  label: group,
                  onClick: () => { setSelectedYearGroup(group) }
                }))
              ]}
            />

      </Box>

      <Divider />


          <Box direction='column' gap='medium' pad={{ horizontal: 'medium' }}>
            <Box align='center'>
              <PulseLoader
                size={15}
                color='orange'
                loading={loading}
              />
            </Box>
            {filteredUsers && filteredUsers.length
              ? (
                <ResponsiveContext.Consumer>
                  {responsive => (
                    <DataTable
                      columns={['xsmall', 'small'].includes(responsive) ? dataColumns.filter(el => el.property !== 'learnerNumber' && el.property !== 'yearGroup') : dataColumns}
                      data={filteredUsers}
                      onSort={setSort}
                      step={2000}
                      // paginate
                      sort={sort}
                      // step={2}
                    />)}
                </ResponsiveContext.Consumer>)
              : <Text margin={{ vertical: 'small' }}>There are no record the match the selected filters.</Text>}
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default ServiceUserList
